import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Edit, Trash2, Move, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InventoryManager() {
  const [properties, setProperties] = useState([]);
  const [areas, setAreas] = useState([]);
  const [maps, setMaps] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [cloningItem, setCloningItem] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0 });
  const mapImageRef = useRef(null);
  const [activeTab, setActiveTab] = useState('inventory');
  const [customTypes, setCustomTypes] = useState([]);
  const [defaultTypes, setDefaultTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [formData, setFormData] = useState({
    label: '',
    type: 'sun_chair',
    zone: '',
    map_x: 0,
    map_y: 0,
    capacity: 2,
    is_free: false,
    base_price: 0,
    preview_title: '',
    preview_description: '',
    preview_image_url: '',
    amenities: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      // Clear previous selections when property changes
      setSelectedArea('');
      setInventory([]);
      setMaps([]);
      setCurrentMap(null);
      fetchAreas();
      fetchInventoryTypes();
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedArea) {
      // Clear previous data when area changes
      setInventory([]);
      setMaps([]);
      setCurrentMap(null);
      fetchInventory();
      fetchMaps();
    }
  }, [selectedArea]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API_URL}/areas?property_id=${selectedProperty}`);
      setAreas(response.data);
      if (response.data.length > 0) {
        setSelectedArea(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const fetchMaps = async () => {
    try {
      const response = await axios.get(`${API_URL}/maps?area_id=${selectedArea}`);
      if (response.data.length > 0) {
        setMaps(response.data);
        setCurrentMap(response.data[0]);
      } else {
        setMaps([]);
        setCurrentMap(null);
      }
    } catch (error) {
      toast.error('Failed to load maps');
    }
  };

  const fetchInventoryTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory-types?property_id=${selectedProperty}`);
      setCustomTypes(response.data.custom_types || []);
      setDefaultTypes(response.data.default_types || []);
    } catch (error) {
      console.error('Failed to load inventory types:', error);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inventory?area_id=${selectedArea}`);
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleMapLoad = () => {
    if (mapImageRef.current) {
      setMapDimensions({
        width: mapImageRef.current.offsetWidth,
        height: mapImageRef.current.offsetHeight
      });
    }
  };

  const handleMapClick = (e) => {
    if (!currentMap || !mapImageRef.current) return;

    const rect = mapImageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert to original map coordinates
    const mapX = (x / rect.width) * currentMap.width;
    const mapY = (y / rect.height) * currentMap.height;

    setFormData({
      ...formData,
      map_x: Math.round(mapX),
      map_y: Math.round(mapY)
    });
    
    toast.success(`Position set: X=${Math.round(mapX)}, Y=${Math.round(mapY)}`);
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!draggedItem || !currentMap || !mapImageRef.current) return;

    const rect = mapImageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert to original map coordinates
    const mapX = (x / rect.width) * currentMap.width;
    const mapY = (y / rect.height) * currentMap.height;

    try {
      await axios.put(`${API_URL}/inventory/${draggedItem.id}`, {
        map_x: Math.round(mapX),
        map_y: Math.round(mapY)
      });
      toast.success(`${draggedItem.label} moved to new position`);
      fetchInventory();
      setDraggedItem(null);
    } catch (error) {
      toast.error('Failed to move item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        area_id: selectedArea,
        label: formData.label,
        type: formData.type,
        zone: formData.zone,
        map_x: parseFloat(formData.map_x),
        map_y: parseFloat(formData.map_y),
        capacity: parseInt(formData.capacity),
        is_free: formData.is_free,
        base_price: parseFloat(formData.base_price),
        preview_title: formData.preview_title,
        preview_description: formData.preview_description,
        preview_image_url: formData.preview_image_url,
        amenities: formData.amenities ? formData.amenities.split(',').map(a => a.trim()).filter(a => a) : []
      };

      if (editingItem) {
        // Update existing item
        await axios.put(`${API_URL}/inventory/${editingItem.id}`, data);
        toast.success('Inventory item updated successfully');
      } else {
        // Create new item (including clones)
        await axios.post(`${API_URL}/inventory`, data);
        if (cloningItem) {
          toast.success(`Cloned from ${cloningItem.label} - New item created: ${data.label}`);
        } else {
          toast.success('Inventory item created successfully');
        }
      }

      setDialogOpen(false);
      setEditingItem(null);
      setCloningItem(null);
      resetForm();
      fetchInventory();
    } catch (error) {
      console.error('Save error:', error);
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error('Failed to save inventory item: ' + errorMessage);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setCloningItem(null);
    setFormData({
      label: item.label,
      type: item.type,
      zone: item.zone || '',
      map_x: item.map_x,
      map_y: item.map_y,
      capacity: item.capacity,
      is_free: item.is_free,
      base_price: item.base_price,
      preview_title: item.preview_title || '',
      preview_description: item.preview_description || '',
      preview_image_url: item.preview_image_url || '',
      amenities: (item.amenities || []).join(', ')
    });
    setDialogOpen(true);
  };

  const handleClone = (item) => {
    setCloningItem(item);
    setEditingItem(null);
    // Copy ALL fields from the original item
    setFormData({
      label: item.label + ' Copy',
      type: item.type,
      zone: item.zone || '',
      map_x: item.map_x + 50, // Offset slightly so it doesn't overlap
      map_y: item.map_y + 50,
      capacity: item.capacity,
      is_free: item.is_free,
      base_price: item.base_price,
      preview_title: item.preview_title || '',
      preview_description: item.preview_description || '',
      preview_image_url: item.preview_image_url || '',
      amenities: (item.amenities || []).join(', ')
    });
    setDialogOpen(true);
    toast.info('Cloning item - all fields copied. Update the label and position as needed.');
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API_URL}/inventory/${itemId}`);
      toast.success('Inventory item deleted');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      type: 'sun_chair',
      zone: '',
      map_x: 0,
      map_y: 0,
      capacity: 2,
      is_free: false,
      base_price: 0,
      preview_title: '',
      preview_description: '',
      preview_image_url: '',
      amenities: ''
    });
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setCloningItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const getItemColor = (item) => {
    if (item.status === 'out_of_order') return '#EF4444';
    if (item.is_free) return '#10B981';
    return '#9F1239';
  };

  return (
    <AdminLayout title="Inventory Management">
      <div className="mb-6 flex gap-4 items-end">
        <div className="flex-1">
          <Label htmlFor="property">Property</Label>
          <select
            id="property"
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
            data-testid="select-property"
          >
            {properties.map(prop => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <Label htmlFor="area">Area</Label>
          <select
            id="area"
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
            className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
            data-testid="select-area"
          >
            {areas.map(area => (
              <option key={area.id} value={area.id}>{area.name}</option>
            ))}
          </select>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="bg-[#9F1239] hover:bg-[#881337] text-white"
              data-testid="btn-add-inventory"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Inventory
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingItem ? 'Edit Inventory Item' : cloningItem ? 'Clone Inventory Item' : 'Add New Inventory Item'}
              </DialogTitle>
              {cloningItem && (
                <p className="text-sm text-[#78716C]">Cloning from: {cloningItem.label}</p>
              )}
            </DialogHeader>
            
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Item Details</TabsTrigger>
                <TabsTrigger value="map">Map Position</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="label">Label *</Label>
                      <Input
                        id="label"
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder="e.g., A1, Cabana-1"
                        required
                        data-testid="input-label"
                      />
                    </div>
                    <div>
                      <Label htmlFor="type">Type *</Label>
                      <select
                        id="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full p-2 border border-[#E7E5E4] rounded-sm"
                        required
                        data-testid="select-type"
                      >
                        <optgroup label="Default Types">
                          {defaultTypes.map(type => (
                            <option key={type.slug} value={type.slug}>{type.name}</option>
                          ))}
                        </optgroup>
                        {customTypes.length > 0 && (
                          <optgroup label="Custom Types">
                            {customTypes.map(type => (
                              <option key={type.slug} value={type.slug}>{type.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="zone">Zone</Label>
                      <Input
                        id="zone"
                        value={formData.zone}
                        onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                        placeholder="VIP, Standard, Premium"
                        data-testid="input-zone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="capacity">Capacity *</Label>
                      <Input
                        id="capacity"
                        type="number"
                        value={formData.capacity}
                        onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                        min="1"
                        required
                        data-testid="input-capacity"
                      />
                    </div>
                    <div>
                      <Label htmlFor="base_price">Base Price ($)</Label>
                      <Input
                        id="base_price"
                        type="number"
                        step="0.01"
                        value={formData.base_price}
                        onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                        data-testid="input-price"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        id="is_free"
                        checked={formData.is_free}
                        onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="checkbox-free"
                      />
                      <Label htmlFor="is_free">Free Item (24hr booking only)</Label>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor="preview_title">Preview Title</Label>
                    <Input
                      id="preview_title"
                      value={formData.preview_title}
                      onChange={(e) => setFormData({ ...formData, preview_title: e.target.value })}
                      placeholder="Premium Poolside Cabana"
                      data-testid="input-preview-title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="preview_description">Preview Description</Label>
                    <Textarea
                      id="preview_description"
                      value={formData.preview_description}
                      onChange={(e) => setFormData({ ...formData, preview_description: e.target.value })}
                      placeholder="Luxury poolside seating with premium amenities..."
                      rows={3}
                      data-testid="textarea-description"
                    />
                  </div>

                  <div>
                    <Label htmlFor="amenities">Amenities (comma-separated)</Label>
                    <Input
                      id="amenities"
                      value={formData.amenities}
                      onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                      placeholder="WiFi, Towel Service, Drinks, Shade"
                      data-testid="input-amenities"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#9F1239] hover:bg-[#881337]"
                    data-testid="btn-submit"
                  >
                    {editingItem ? 'Update Item' : cloningItem ? `Clone as "${formData.label}"` : 'Create Item'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="map">
                {currentMap ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Click on the map</strong> to set the position for this item.
                      </p>
                      <p className="text-sm text-blue-700 mt-2">
                        Current Position: X={formData.map_x}, Y={formData.map_y}
                      </p>
                    </div>
                    <div 
                      className="relative cursor-crosshair border-2 border-[#9F1239] rounded-sm overflow-hidden"
                      onClick={handleMapClick}
                    >
                      <img
                        ref={mapImageRef}
                        src={currentMap.file_url}
                        alt="Area Map"
                        className="w-full"
                        onLoad={handleMapLoad}
                      />
                      {formData.map_x > 0 && formData.map_y > 0 && mapDimensions.width > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${(formData.map_x / currentMap.width) * 100}%`,
                            top: `${(formData.map_y / currentMap.height) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-[#9F1239] border-3 border-white shadow-xl flex items-center justify-center">
                            <span className="text-white text-[10px] font-bold">{formData.label || '?'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Map X</Label>
                        <Input
                          type="number"
                          value={formData.map_x}
                          onChange={(e) => setFormData({ ...formData, map_x: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label>Map Y</Label>
                        <Input
                          type="number"
                          value={formData.map_y}
                          onChange={(e) => setFormData({ ...formData, map_y: parseFloat(e.target.value) })}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-[#78716C]">No map uploaded for this area yet. Upload a map in Map Management first.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map View */}
          {currentMap && (
            <div className="lg:col-span-2">
              <Card className="bg-white border-slate-200 shadow-sm rounded-md">
                <CardHeader>
                  <CardTitle className="text-xl font-serif flex items-center gap-2">
                    <Move className="w-5 h-5" />
                    Drag & Drop to Reposition
                  </CardTitle>
                  <p className="text-sm text-[#78716C]">Drag markers to new positions on the map</p>
                </CardHeader>
                <CardContent>
                  <div
                    className="relative"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <img
                      ref={mapImageRef}
                      src={currentMap.file_url}
                      alt="Area Map"
                      className="w-full rounded-sm"
                      onLoad={handleMapLoad}
                    />
                    {mapDimensions.width > 0 && inventory.map((item) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        style={{
                          position: 'absolute',
                          left: `${(item.map_x / currentMap.width) * 100}%`,
                          top: `${(item.map_y / currentMap.height) * 100}%`,
                          transform: 'translate(-50%, -50%)',
                          cursor: 'move',
                        }}
                        className="group"
                      >
                        <div
                          className="w-8 h-8 rounded-full border-3 border-white shadow-xl transition-all duration-200 group-hover:scale-125 flex items-center justify-center"
                          style={{ backgroundColor: getItemColor(item) }}
                        >
                          <span className="text-white text-[10px] font-bold">{item.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* List View */}
          <div className={currentMap ? 'lg:col-span-1' : 'lg:col-span-3'}>
            <Card className="bg-white border-slate-200 shadow-sm rounded-md">
              <CardHeader>
                <CardTitle className="text-xl font-serif">Inventory Items</CardTitle>
              </CardHeader>
              <CardContent>
                {inventory.length === 0 ? (
                  <p className="text-center text-[#78716C] py-8">No inventory items yet</p>
                ) : (
                  <div className="space-y-3">
                    {inventory.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 border border-[#E7E5E4] rounded-sm hover:bg-[#FAFAF9]"
                        data-testid={`item-${item.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ backgroundColor: getItemColor(item) }}
                          >
                            {item.label}
                          </div>
                          <div>
                            <p className="font-medium">{item.label}</p>
                            <p className="text-xs text-[#78716C]">{item.type.replace(/_/g, ' ')}</p>
                            <p className="text-xs font-medium text-[#9F1239]">
                              {item.is_free ? 'FREE' : `$${item.base_price}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleClone(item)}
                            className="text-[#0F766E] hover:text-[#0F766E] hover:bg-emerald-50"
                            data-testid={`btn-clone-${item.id}`}
                            title="Clone this item"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            data-testid={`btn-edit-${item.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-delete-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
